import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/videos");
    setVideos(data);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#ff4500] font-mono">── Videos</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 tracking-tighter">Library</h1>
          <p className="text-[#a3a3a3] mt-3 text-sm">Upload MP4 files and stream them anywhere.</p>
        </div>
        <button
          onClick={() => setUploadOpen(true)}
          data-testid="open-video-upload"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#ff4500] hover:bg-[#ff5722] text-white text-xs uppercase tracking-[0.2em] transition-colors"
        >
          <Upload className="w-4 h-4" /> Upload
        </button>
      </div>

      {videos.length === 0 ? (
        <div className="border border-dashed border-[#2a2a2a] p-16 text-center">
          <p className="text-[#a3a3a3] text-sm">No videos yet. Be the first to upload.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {videos.map((v) => <VideoCard key={v.id} video={v} />)}
        </div>
      )}

      {uploadOpen && (
        <UploadVideoDialog onClose={() => setUploadOpen(false)} onDone={() => { load(); setUploadOpen(false); }} />
      )}
    </div>
  );
}

function UploadVideoDialog({ onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general");
  const [thumbnail, setThumbnail] = useState("");
  const [file, setFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState("");
  const [thumbBlob, setThumbBlob] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const captureThumb = (videoFile) => {
    setThumbPreview("");
    setThumbBlob(null);
    if (!videoFile) return;
    const url = URL.createObjectURL(videoFile);
    const v = document.createElement("video");
    v.src = url;
    v.muted = true;
    v.playsInline = true;
    v.crossOrigin = "anonymous";
    v.addEventListener("loadeddata", () => {
      try {
        // Seek slightly in to skip black opening frames
        v.currentTime = Math.min(0.5, (v.duration || 1) / 4);
      } catch {
        v.currentTime = 0;
      }
    });
    v.addEventListener("seeked", () => {
      try {
        const canvas = document.createElement("canvas");
        const w = v.videoWidth || 1280;
        const h = v.videoHeight || 720;
        const maxW = 960;
        const scale = w > maxW ? maxW / w : 1;
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) {
            setThumbBlob(blob);
            setThumbPreview(URL.createObjectURL(blob));
          }
          URL.revokeObjectURL(url);
        }, "image/jpeg", 0.82);
      } catch (e) {
        URL.revokeObjectURL(url);
      }
    });
    v.load();
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (f) captureThumb(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Choose an MP4 file"); return; }
    if (!file.name.toLowerCase().endsWith(".mp4")) { setErr("Only MP4 files are allowed"); return; }
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("description", description);
      fd.append("category", category);
      fd.append("thumbnail_url", thumbnail);
      if (thumbBlob) fd.append("thumbnail_file", thumbBlob, "thumb.jpg");
      await api.post("/videos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || 1)) * 100)),
      });
      toast.success("Video uploaded");
      onDone();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#141414] border border-[#2a2a2a] reveal max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2a2a] sticky top-0 bg-[#141414]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] font-mono">// Upload video</div>
          <button onClick={onClose} data-testid="close-video-upload" className="text-[#a3a3a3] hover:text-[#f5f5f5]"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Title</label>
            <input required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="video-title-input"
              className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} data-testid="video-desc-input"
              className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm resize-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Culture category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="video-category-input"
              className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm appearance-none"
            >
              <option value="general">General · Tout sa ki gen</option>
              <option value="history">History · Istwa Ayiti</option>
              <option value="geography">Geography · Jewografi</option>
              <option value="carnival">Carnival & Festivals · Kanaval</option>
              <option value="cuisine">Cuisine · Manje Ayisyen</option>
              <option value="arts">Arts & Crafts · Atizay</option>
              <option value="music">Music & Dance · Mizik</option>
            </select>
            <div className="text-[10px] text-[#a3a3a3] font-mono mt-1.5">// Tagging helps your video appear on the matching Esplore Ayiti page</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Thumbnail URL (optional · used if auto-capture fails)</label>
            <input value={thumbnail} onChange={(e) => setThumbnail(e.target.value)} data-testid="video-thumb-input"
              placeholder="https://…"
              className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">MP4 file (max 200MB)</label>
            <div className="mt-2 border border-dashed border-[#2a2a2a] p-4 flex items-center justify-between">
              <span className="text-xs text-[#a3a3a3] truncate" data-testid="video-file-label">{file ? file.name : "No file chosen"}</span>
              <button type="button" onClick={() => inputRef.current?.click()} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500]">
                Choose
              </button>
              <input ref={inputRef} type="file" accept="video/mp4" hidden onChange={onFileChange} data-testid="video-file-input" />
            </div>
          </div>

          {thumbPreview && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Auto thumbnail</div>
              <div className="mt-2 border border-[#2a2a2a] bg-black aspect-video overflow-hidden" data-testid="thumb-preview">
                <img src={thumbPreview} alt="thumb" className="w-full h-full object-cover" />
              </div>
            </div>
          )}

          {uploading && (
            <div>
              <div className="h-1 bg-[#2a2a2a]"><div className="h-1 bg-[#ff4500]" style={{ width: `${progress}%` }} /></div>
              <div className="text-[10px] font-mono text-[#a3a3a3] mt-2">{progress}%</div>
            </div>
          )}
          {err && <div className="text-xs text-[#ff0033]" data-testid="video-upload-error">{err}</div>}

          <button type="submit" disabled={uploading} data-testid="submit-video-upload"
            className="w-full px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] disabled:opacity-50 text-white text-sm font-medium">
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </form>
      </div>
    </div>
  );
}
